import React, { useEffect, useState, createContext, useContext } from 'react';
interface CurrencyContextType {
  currencyCode: string;
  exchangeRate: number;
  isLoading: boolean;
  formatPrice: (usdAmount: number) => string;
  debugInfo: string;
  setManualCurrency: (code: string) => void;
  availableCurrencies: {
    code: string;
    label: string;
    symbol: string;
  }[];
}
const CurrencyContext = createContext<CurrencyContextType>({
  currencyCode: 'USD',
  exchangeRate: 1,
  isLoading: true,
  formatPrice: amount => `$${amount.toFixed(2)}`,
  debugInfo: '',
  setManualCurrency: () => {},
  availableCurrencies: []
});
export const useCurrency = () => useContext(CurrencyContext);
// Only the 5 countries the user needs
const SUPPORTED_CURRENCIES = [{
  code: 'USD',
  label: '🇺🇸 USD',
  symbol: '$',
  country: 'US'
}, {
  code: 'AUD',
  label: '🇦🇺 AUD',
  symbol: 'A$',
  country: 'AU'
}, {
  code: 'NZD',
  label: '🇳🇿 NZD',
  symbol: 'NZ$',
  country: 'NZ'
}, {
  code: 'GBP',
  label: '🇬🇧 GBP',
  symbol: '£',
  country: 'GB'
}, {
  code: 'CAD',
  label: '🇨🇦 CAD',
  symbol: 'CA$',
  country: 'CA'
}];
const countryToCurrencyCode: Record<string, string> = {
  US: 'USD',
  AU: 'AUD',
  NZ: 'NZD',
  GB: 'GBP',
  CA: 'CAD'
};
// Try multiple IP geolocation APIs
async function detectCountryFromIP(): Promise<string | null> {
  // Attempt 1: api.country.is (very lightweight, CORS-friendly)
  try {
    const res = await fetch('https://api.country.is/', {
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.country) return data.country;
    }
  } catch (e) {
    console.warn('api.country.is failed:', e);
  }
  // Attempt 2: ipapi.co
  try {
    const res = await fetch('https://ipapi.co/country/', {
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      const country = (await res.text()).trim();
      if (country && country.length === 2) return country;
    }
  } catch (e) {
    console.warn('ipapi.co failed:', e);
  }
  // Attempt 3: ipwho.is
  try {
    const res = await fetch('https://ipwho.is/', {
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success !== false && data.country_code) return data.country_code;
    }
  } catch (e) {
    console.warn('ipwho.is failed:', e);
  }
  // Attempt 4: freeipapi.com
  try {
    const res = await fetch('https://freeipapi.com/api/json', {
      signal: AbortSignal.timeout(4000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.countryCode) return data.countryCode;
    }
  } catch (e) {
    console.warn('freeipapi.com failed:', e);
  }
  return null;
}
async function fetchExchangeRates(): Promise<Record<string, number> | null> {
  // Try primary API
  try {
    const res = await fetch('https://open.er-api.com/v6/latest/USD', {
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.rates) return data.rates;
    }
  } catch (e) {
    console.warn('open.er-api.com failed:', e);
  }
  // Try fallback API
  try {
    const res = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json', {
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.usd) {
        // Convert keys to uppercase to match our format
        const rates: Record<string, number> = {};
        for (const [key, val] of Object.entries(data.usd)) {
          rates[key.toUpperCase()] = val as number;
        }
        return rates;
      }
    }
  } catch (e) {
    console.warn('Fallback exchange rate API failed:', e);
  }
  return null;
}
// Hardcoded approximate rates as absolute last resort
const FALLBACK_RATES: Record<string, number> = {
  USD: 1,
  AUD: 1.55,
  NZD: 1.71,
  GBP: 0.79,
  CAD: 1.36
};
export const CurrencyProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [rates, setRates] = useState<Record<string, number>>(FALLBACK_RATES);
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState('');
  useEffect(() => {
    const init = async () => {
      // 1. Detect country from IP
      const country = await detectCountryFromIP();
      const detectedCurrency = country ? countryToCurrencyCode[country] || 'USD' : 'USD';
      setDebugInfo(`IP Country: ${country || 'unknown'} → ${detectedCurrency}`);
      setCurrencyCode(detectedCurrency);
      // 2. Fetch live exchange rates
      const liveRates = await fetchExchangeRates();
      if (liveRates) {
        setRates(liveRates);
        setDebugInfo(prev => `${prev} | Live rates ✓`);
      } else {
        setDebugInfo(prev => `${prev} | Using fallback rates`);
      }
      setIsLoading(false);
    };
    init();
  }, []);
  const setManualCurrency = (code: string) => {
    setCurrencyCode(code);
    setDebugInfo(prev => `${prev} | Manual: ${code}`);
  };
  const exchangeRate = rates[currencyCode] || 1;
  const formatPrice = (usdAmount: number) => {
    const convertedAmount = usdAmount * exchangeRate;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(convertedAmount);
    } catch (e) {
      const info = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
      return `${info?.symbol || '$'}${convertedAmount.toFixed(2)}`;
    }
  };
  return <CurrencyContext.Provider value={{
    currencyCode,
    exchangeRate,
    isLoading,
    formatPrice,
    debugInfo,
    setManualCurrency,
    availableCurrencies: SUPPORTED_CURRENCIES
  }} data-id="element-371">
      {children}
    </CurrencyContext.Provider>;
};